import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './MemberProfile.css';
import apiConfig from '../../components/config/apiConfig';
import { fetchWithAuth, addAuthHeaders } from '../../utils/fetchWithAuth';

const MemberProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [passwordChange, setPasswordChange] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    membershipType: '',
    membershipStatus: '',
    memberSince: '',
    membershipExpiry: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Add fetchUserData function to refresh member data
  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const memberId = localStorage.getItem('memberId');
      
      // Use member profile endpoint from apiConfig with correct structure
      const apiUrl = `${apiConfig.MEMBER.PROFILE}?memberId=${memberId}`;
      
      const response = await fetchWithAuth(apiUrl, addAuthHeaders());

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch member data');
      }

      const data = await response.json();
      
      // Format dates properly if needed
      const memberSince = data.memberSince ? new Date(data.memberSince).toLocaleDateString() : 'N/A';
      const membershipExpiry = data.membershipExpiry ? new Date(data.membershipExpiry).toLocaleDateString() : 'N/A';
      
      // Split name into first name and last name if it comes as full name
      let firstName = data.firstName || '';
      let lastName = data.lastName || '';
      
      if (!firstName && !lastName && data.name) {
        const nameParts = data.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      setFormData({
        firstName,
        lastName,
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zipCode || '',
        membershipType: data.membershipType || 'N/A',
        membershipStatus: data.status || 'N/A',
        membershipNumber: data.membershipNumber || 'N/A',
        memberSince,
        membershipExpiry,
        professionalListing: data.professionalListing || null
      });
      
      setLoading(false);
      
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Check if user is logged in and fetch member data
  useEffect(() => {
    const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
    const memberId = localStorage.getItem('memberId');
    
    if (!token || !memberId) {
      navigate('/member-login');
      return;
    }

    fetchUserData();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  console.log('formData', formData);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    setSuccessMessage('');
  };

  const togglePasswordChange = () => {
    setPasswordChange(!passwordChange);
    setSuccessMessage('');
    
    // Reset password fields when toggling
    if (!passwordChange) {
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const memberId = localStorage.getItem('memberId');
      
      // Use member profile endpoint for updates with correct structure
      const response = await fetchWithAuth(apiConfig.MEMBER.PROFILE, addAuthHeaders({
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          memberId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        })
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      // Update the stored name in local storage if it changed
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      if (fullName !== localStorage.getItem('memberName')) {
        localStorage.setItem('memberName', fullName);
        // Trigger storage event for Header component to update
        window.dispatchEvent(new Event('storage'));
      }
      
      setSuccessMessage('Profile updated successfully');
      setIsEditing(false);
      
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      // Use change-password endpoint with correct structure
      const response = await fetchWithAuth(apiConfig.MEMBER.CHANGE_PASSWORD, addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }

      setSuccessMessage('Password updated successfully');
      setPasswordChange(false);
      
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRenewalModalOpen = () => {
    setShowRenewalModal(true);
  };

  const handleRenewalModalClose = () => {
    setShowRenewalModal(false);
  };

  const handleRenewMembership = async () => {
    try {
      setRenewalLoading(true);
      // Use the correct member renew-membership endpoint
      const response = await fetchWithAuth(apiConfig.MEMBER.RENEW_MEMBERSHIP, addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to renew membership');
      }

      const orderData = await response.json();
      
      // Initialize Razorpay payment
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Bangalore Valuers Association',
        description: 'Membership Renewal',
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          try {
            // Show loading state while verifying payment
            setRenewalLoading(true);
            
            // Verify payment with server using correct structure
            const verifyResponse = await fetchWithAuth(apiConfig.PUBLIC.PAYMENT_VERIFICATION, addAuthHeaders({
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            }));
            
            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }
            
            const verifyData = await verifyResponse.json();
            
            // Update membership expiry in form data
            if (verifyData.success) {
              // Close the renewal modal first
              setShowRenewalModal(false);
              
              // Show success message
              setSuccessMessage('🎉 Membership renewed successfully! Your membership has been extended.');
              setError(null); // Clear any previous errors
              
              // Update membership expiry if provided
              if (verifyData.member && verifyData.member.expiryDate) {
                const newExpiryDate = new Date(verifyData.member.expiryDate).toLocaleDateString();
                setFormData(prev => ({
                  ...prev,
                  membershipExpiry: newExpiryDate
                }));
              }
              
              // Refresh user data to get updated information
              await fetchUserData();
              
              // Scroll to top to make sure user sees the success message
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              throw new Error(verifyData.message || 'Payment verification failed');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            setShowRenewalModal(false);
            setError(`Payment verification failed: ${err.message}. Please contact support if payment was deducted.`);
            // Scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } finally {
            // Hide loading state after verification completes
            setRenewalLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            // Handle when user closes the payment modal without completing payment
            setRenewalLoading(false);
            setShowRenewalModal(false);
            console.log('Payment modal was closed by user');
          }
        },
        prefill: {
          name: formData.firstName + ' ' + formData.lastName,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#002147'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      setError(err.message || 'Payment initiation failed');
    } finally {
      setRenewalLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="member-profile-container">
      {/* Payment Verification Loading Overlay */}
      {renewalLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginTop: '1.5rem',
            fontWeight: '500'
          }}>
            Verifying your payment...
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, this may take a few moments
          </p>
        </div>
      )}
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div className="profile-header">
        <div className="header-left">
          <button className="btn btn-outline-secondary back-btn" onClick={handleGoBack}>
            &larr; Back to Home
          </button>
          <h1>My Profile</h1>
        </div>
        {!isEditing && !passwordChange && (
          <button className="btn btn-primary edit-btn" onClick={toggleEditMode}>
            Edit Profile
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
        
      <div className="profile-content">
        <div className="membership-info-card">
          <h2>Membership Information</h2>
          <div className="membership-details">
             <div className="detail-item">
              <span className="label">Membership Number:</span>
              <span className="value">{formData.membershipNumber}</span>
            </div>
            <div className="detail-item">
              <span className="label">Membership Type:</span>
              <span className="value">{formData.membershipType}</span>
            </div>
            <div className="detail-item">
              <span className="label">Status:</span>
              <span className={`value status ${formData.membershipStatus.toLowerCase()}`}>
                {formData.membershipStatus}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Member Since:</span>
              <span className="value">{formData.memberSince}</span>
            </div>
            <div className="detail-item">
              <span className="label">Membership Validity Date:</span>
              <span className="value">{formData.membershipExpiry}</span>
            </div>
          </div>
          
          {/* Renew Membership Button */}
          {formData.membershipStatus.toLowerCase() !== 'lifetime' && (
            <div className="renewal-section">
              <button 
                className="btn btn-success renew-btn" 
                onClick={handleRenewalModalOpen}
              >
                Renew Membership
              </button>
            </div>
          )}
          
          {/* Professional Listing Status */}
          {formData.professionalListing && (
            <div className="professional-listing-section" style={{
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f5f8ff',
              borderRadius: '8px',
              border: '1px solid #e1e7ff'
            }}>
              <h4 style={{ marginTop: 0, color: '#2c5282' }}>Professional Listing</h4>
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className={`value status ${formData.professionalListing.status.toLowerCase()}`}>
                  {formData.professionalListing.status}
                </span>
              </div>
              {formData.professionalListing.validUntil && (
                <div className="detail-item">
                  <span className="label">Valid Until:</span>
                  <span className="value">{formData.professionalListing.validUntil}</span>
                </div>
              )}
              <div style={{ marginTop: '1rem' }}>
                <Link to="/professionals/my-listing" className="btn btn-outline-primary" style={{ fontSize: '0.9rem' }}>
                  Manage Listing
                </Link>
              </div>
            </div>
          )}
        </div>

        {!passwordChange ? (
          <div className="personal-info-card">
            <h2>Personal Information</h2>
            <form onSubmit={handleProfileUpdate}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled={true} // Email should not be editable
                />
                <small>Contact administrator to change your email address</small>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zipCode">ZIP Code</label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={parseInt(formData.zipCode)}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              {isEditing ? (
                <div className="button-group">
                  <button type="submit" className="btn btn-success">
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={toggleEditMode}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={togglePasswordChange}
                >
                  Change Password
                </button>
              )}
            </form>
          </div>
        ) : (
          <div className="password-change-card">
            <h2>Change Password</h2>
            <form onSubmit={handlePasswordUpdate}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                />
                <small>Password must be at least 8 characters long</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="button-group">
                <button type="submit" className="btn btn-success">
                  Update Password
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={togglePasswordChange}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Membership Renewal Modal */}
      {showRenewalModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Renew Membership</h3>
              <button 
                className="close-btn"
                onClick={handleRenewalModalClose}
                disabled={renewalLoading}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to renew your membership?</p>
              <p>Your membership will be extended for another year from your current expiry date.</p>
              <p>Membership Type: <strong>{formData.membershipType}</strong></p>
              <p>Current Expiry: <strong>{formData.membershipExpiry}</strong></p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleRenewalModalClose}
                disabled={renewalLoading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleRenewMembership}
                disabled={renewalLoading}
              >
                {renewalLoading ? 'Processing...' : 'Confirm Renewal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberProfile;
